import { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useSuspenseQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useToast } from '@/hooks/useToast';
import {
  googleIntegrationScopesQueryOptions,
  useConnectGoogleServiceMutation,
  useDisconnectGoogleServiceMutation,
} from '@/lib/query-options';
import type { GoogleService } from '@/lib/types';

export const GOOGLE_SERVICE_NAMES: Record<GoogleService, string> = {
  google_analytics: 'Google Analytics',
  tag_manager: 'Tag Manager',
};

/**
 * One Google service's own connection for the active organization (#6879): Google Analytics or Tag Manager, from
 * whichever Google account manages it (an agency's, say). The popup asks for that service's scope only, offers the
 * account chooser, and never touches the Google Ads connection. Toasts follow Google Ads: errors, and a disconnect.
 */
export function useGoogleServiceConnection(service: GoogleService, { onConnected }: { onConnected?: () => void } = {}) {
  const posthog = usePostHog();
  const { toast } = useToast();
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const { data: consent } = useSuspenseQuery(googleIntegrationScopesQueryOptions());
  const scope = consent.service_scopes[service];
  const name = GOOGLE_SERVICE_NAMES[service];
  const connectMutation = useConnectGoogleServiceMutation();
  const disconnectMutation = useDisconnectGoogleServiceMutation();

  const oauthLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        // Google lists each permission as its own tickbox; an unticked one comes back missing from the grant.
        if (!codeResponse.scope?.split(' ').includes(scope)) {
          toast({
            title: `${name} Permission Required`,
            description: `${name} access was not granted. Click Connect again and tick the ${name} permission on the Google screen.`,
            variant: 'destructive',
          });
          return;
        }
        await connectMutation.mutateAsync({ service, code: codeResponse.code });
        posthog?.capture('google_service_connected', { service });
        onConnected?.();
      } catch (error) {
        toast({
          title: 'Connection Failed',
          description: error instanceof Error ? error.message : `${name} could not be connected.`,
          variant: 'destructive',
        });
      } finally {
        setIsAuthorizing(false);
      }
    },
    onError: () => setIsAuthorizing(false),
    onNonOAuthError: () => setIsAuthorizing(false),
    scope,
    flow: 'auth-code',
    include_granted_scopes: false,
    select_account: true,
  });

  const connect = () => {
    setIsAuthorizing(true);
    oauthLogin();
  };

  const disconnect = () =>
    disconnectMutation.mutate(service, {
      onSuccess: () => {
        posthog?.capture('google_service_disconnected', { service });
        toast({ title: `${name} Disconnected` });
      },
      onError: (error) =>
        toast({
          title: 'Disconnect Failed',
          description: error.message || `${name} could not be disconnected.`,
          variant: 'destructive',
        }),
    });

  return { connect, disconnect, isAuthorizing, isDisconnecting: disconnectMutation.isPending };
}
